import { useState, useMemo } from 'react';
import {
    Folder,
    FileText,
    ChevronRight,
    ChevronDown,
    Trash2,
    Edit2,
    FileCode,
    FileImage,
    MoreVertical
} from 'lucide-react';
import { formatBytes } from '@wranglr/shared';

interface FileNode {
    name: string;
    path: string; // Full relative path
    type: 'file' | 'directory';
    size?: number;
    children?: FileNode[];
}

interface FileTreeProps {
    files: { name: string; size: number; modified?: string }[];
    onEdit: (path: string) => void;
    onDelete: (path: string) => void;
}

export const FileTree = ({ files, onEdit, onDelete }: FileTreeProps) => {
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

    // Convert flat list to tree
    const tree = useMemo(() => {
        const root: FileNode[] = [];
        const map = new Map<string, FileNode>();

        // Sort files to ensure folders come first
        const sortedFiles = [...files].sort((a, b) => a.name.localeCompare(b.name));

        sortedFiles.forEach(file => {
            const parts = file.name.split('/');
            let currentLevel = root;
            let currentPath = '';

            parts.forEach((part, index) => {
                const isLast = index === parts.length - 1;
                currentPath = currentPath ? `${currentPath}/${part}` : part;

                // Check if node exists at this level
                let existingNode = currentLevel.find(n => n.name === part);

                if (!existingNode) {
                    const newNode: FileNode = {
                        name: part,
                        path: currentPath,
                        type: isLast ? 'file' : 'directory',
                        size: isLast ? file.size : undefined,
                        children: isLast ? undefined : [] // Initialize directory children
                    };

                    if (isLast) {
                        currentLevel.push(newNode);
                    } else {
                        // Directory - check if we already created it (could be out of order or implicit)
                        // Actually, my logic above `currentLevel.find` handles it.
                        currentLevel.push(newNode);
                        existingNode = newNode;
                    }
                }

                if (!isLast && existingNode) {
                    // It's a directory, go deeper
                    if (!existingNode.children) existingNode.children = [];
                    currentLevel = existingNode.children;
                }
            });
        });

        // Helper to sort nodes: Directories first, then files
        const sortNodes = (nodes: FileNode[]) => {
            nodes.sort((a, b) => {
                if (a.type === b.type) return a.name.localeCompare(b.name);
                return a.type === 'directory' ? -1 : 1;
            });
            nodes.forEach(n => {
                if (n.children) sortNodes(n.children);
            });
        };
        sortNodes(root);

        return root;
    }, [files]);

    const toggleFolder = (path: string) => {
        const newSet = new Set(expandedFolders);
        if (newSet.has(path)) {
            newSet.delete(path);
        } else {
            newSet.add(path);
        }
        setExpandedFolders(newSet);
    };

    const getIcon = (name: string, type: 'file' | 'directory') => {
        if (type === 'directory') return <Folder size={16} className="text-blue-400" fill="currentColor" fillOpacity={0.2} />;
        if (name.endsWith('.html')) return <FileCode size={16} className="text-orange-400" />;
        if (name.endsWith('.css')) return <FileCode size={16} className="text-blue-300" />;
        if (name.endsWith('.js') || name.endsWith('.ts') || name.endsWith('.tsx')) return <FileCode size={16} className="text-yellow-400" />;
        if (name.match(/\.(png|jpg|jpeg|gif|svg|webp)$/i)) return <FileImage size={16} className="text-purple-400" />;
        return <FileText size={16} className="text-gray-400" />;
    };

    const renderNode = (node: FileNode, depth: number) => {
        const isFolder = node.type === 'directory';
        const isExpanded = expandedFolders.has(node.path);
        const paddingLeft = `${depth * 1.5}rem`;

        return (
            <div key={node.path}>
                <div
                    className={`file-row ${isFolder ? 'folder-row' : 'file-item'}`}
                    style={{
                        paddingLeft,
                        display: 'flex',
                        alignItems: 'center',
                        paddingTop: '0.5rem',
                        paddingBottom: '0.5rem',
                        borderBottom: '1px solid var(--border-color)',
                        cursor: isFolder ? 'pointer' : 'default',
                        userSelect: 'none'
                    }}
                    onClick={() => isFolder && toggleFolder(node.path)}
                >
                    <div style={{ marginRight: '0.5rem', display: 'flex', alignItems: 'center' }}>
                        {isFolder && (
                            <span style={{ marginRight: '0.25rem' }}>
                                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </span>
                        )}
                        {!isFolder && <span style={{ width: '14px', marginRight: '0.25rem' }} />} {/* Spacer */}
                        {getIcon(node.name, node.type)}
                    </div>

                    <span style={{ flex: 1, fontWeight: isFolder ? 600 : 400, fontSize: '0.9rem' }}>
                        {node.name}
                    </span>

                    {!isFolder && (
                        <div className="file-meta" style={{ display: 'flex', gap: '1rem', alignItems: 'center', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            <span>{formatBytes(node.size || 0)}</span>
                            <div className="actions" style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                    className="btn-icon"
                                    onClick={(e) => { e.stopPropagation(); onEdit(node.path); }}
                                    title="Edit File"
                                    style={{ padding: '4px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-primary)' }}
                                >
                                    <Edit2 size={14} />
                                </button>
                                <button
                                    className="btn-icon"
                                    onClick={(e) => { e.stopPropagation(); onDelete(node.path); }}
                                    title="Delete File"
                                    style={{ padding: '4px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--accent-danger)' }}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {isFolder && isExpanded && node.children && (
                    <div className="folder-children">
                        {node.children.map(child => renderNode(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    if (files.length === 0) {
        return <div className="empty-state">No files uploaded yet.</div>;
    }

    return (
        <div className="file-tree-container" style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden', background: 'var(--bg-card)' }}>
            <div className="file-tree-header" style={{ padding: '0.75rem 1rem', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)', fontWeight: 600, fontSize: '0.85rem' }}>
                Files & Folders
            </div>
            {tree.map(node => renderNode(node, 0))}
        </div>
    );
};
